import { useCallback } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createContainer,
	createHook,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';
import type { State } from './types.tsx';

export const createSwitchController = <T,>(name: string, activeByDefault?: T) => {
	const actions = {
		on:
			(active: unknown) =>
			({ setState }: StoreActionApi<State>) =>
				setState({ active }),
		off:
			(active: unknown) =>
			({ setState, getState }: StoreActionApi<State>) => {
				if (getState().active === active || active === undefined) {
					setState({ active: undefined });
				}
			},
		toggle:
			(active: unknown) =>
			({ getState, dispatch }: StoreActionApi<State>) => {
				if (getState().active === active) {
					dispatch(actions.off(active));
				} else {
					dispatch(actions.on(active));
				}
			},
	};

	const initialState: State = { active: activeByDefault };

	const store = createStore<State, typeof actions>({
		name,
		initialState,
		actions,
	});

	const SwitchContainer = createContainer(store);

	const selector = (state: State, key: unknown) => state.active === key;

	const useSwitchInternal = createHook(store, {
		selector,
	});

	const useSwitch = (active?: T) => {
		const [isActive, { on: onSwitch, off: offSwitch, toggle: toggleSwitch }] =
			useSwitchInternal(active);
		const on = useCallback(() => onSwitch(active), [onSwitch, active]);
		const off = useCallback(() => offSwitch(active), [active, offSwitch]);
		const toggle = useCallback(() => toggleSwitch(active), [toggleSwitch, active]);

		return [isActive, { on, off, toggle }] as const;
	};

	return { SwitchContainer, useSwitch };
};
